import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, message, Select, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';

import { formatUtcToLocal } from '../../../utils/timezoneFormatter';

import { getSmartLabelClassName } from '../../../utils/labelWidthUtils';
import {
    useFilterContainerClassName,
    useFilterInputContainerClassName,
    useFilterLabelClassName,
} from '../../../utils/languageUtils';
import { getUserInfo } from '../../../utils/auth';
import TablePaginationBar from '../../../components/TablePaginationBar';
import CentralParcelDetailDrawer from '../../central/parcels/components/CentralParcelDetailDrawer';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
    useAsyncReportExportCooldown,
} from '../../../components/asyncReportDownload';
import {
    GENERATE_WMS_NO_UPDATE_DETAIL_DOWNLOAD_REPORT,
    GenerateWmsNoUpdateDetailDownloadReportResponse,
    GenerateWmsNoUpdateDetailDownloadReportVariables,
} from '../../../graphql/mutations/dashboard/generateWmsNoUpdateDetailDownloadReport';
import { stripOrderEventDataPrefix } from '../../../constants/externalEventCodes';
import { showError } from '../../../utils/exceptionUtils';

import {
    QUERY_WMS_NO_UPDATE_DETAIL_LIST,
    QUERY_WMS_NO_UPDATE_LOCATION_OPTIONS,
} from '../../../graphql/queries/dashboard/queryWmsNoUpdate';
import {
    QueryWmsNoUpdateDetailListResponse,
    QueryWmsNoUpdateLocationOptionsResponse,
    WmsNoUpdateDetailPageRequestInput,
    WmsNoUpdateLocationOptionsInput,
    WmsNoUpdateQueryFilterInput,
} from '../../../graphql/types/dashboard/WmsNoUpdateTypes';
import {
    NoUpdateDetailRow,
    NoUpdateLocationTypeGql,
    NoUpdateStatusInput,
    NoUpdateTypeGql,
} from '../../../graphql/types/dashboard/NoUpdateDashboardTypes';

const { RangePicker } = DatePicker;

const PAGE_SIZE_DEFAULT = 10;

interface TypeOption {
    /** i18n key（拼到 wms.dashboard.noUpdate.type.* 下） */
    labelKey: string;
    type: NoUpdateTypeGql;
}

/** 仓内 tab：3 种 type；Hub 页"仓内"tab 用 */
const IN_HUB_TYPE_OPTIONS: TypeOption[] = [
    { labelKey: 'linehaulInbound', type: NoUpdateTypeGql.LINEHAUL_INBOUND },
    { labelKey: 'inHubOperation', type: NoUpdateTypeGql.IN_HUB_OPERATION },
    { labelKey: 'inHubLinehaul', type: NoUpdateTypeGql.IN_HUB_LINEHAUL },
];

/** 尾程 tab / Warehouse 页：只有 LAST_MILE */
const LAST_MILE_TYPE_OPTIONS: TypeOption[] = [
    { labelKey: 'lastMile', type: NoUpdateTypeGql.LAST_MILE },
];

/** 断更类型 enum → i18n labelKey（与筛选项一致，列展示用） */
const TYPE_LABEL_KEY: Record<NoUpdateTypeGql, string> = {
    [NoUpdateTypeGql.LINEHAUL_INBOUND]: 'linehaulInbound',
    [NoUpdateTypeGql.IN_HUB_OPERATION]: 'inHubOperation',
    [NoUpdateTypeGql.IN_HUB_LINEHAUL]: 'inHubLinehaul',
    [NoUpdateTypeGql.LAST_MILE]: 'lastMile',
};

/** 断更类型 → Tag 颜色（与抽屉 NoUpdateDetailDrawer 保持一致） */
const TYPE_TAG_COLOR: Record<NoUpdateTypeGql, string> = {
    [NoUpdateTypeGql.LINEHAUL_INBOUND]: 'blue',
    [NoUpdateTypeGql.IN_HUB_OPERATION]: 'green',
    [NoUpdateTypeGql.IN_HUB_LINEHAUL]: 'orange',
    [NoUpdateTypeGql.LAST_MILE]: 'purple',
};

/** 断更地点下拉前缀：(hub)/(whs)/(dsp)，用同一份地点列表区分 hub / 仓 / DSP */
const LOCATION_TYPE_PREFIX: Record<NoUpdateLocationTypeGql, string> = {
    [NoUpdateLocationTypeGql.HUB]: '(hub)',
    [NoUpdateLocationTypeGql.WHS]: '(whs)',
    [NoUpdateLocationTypeGql.DSP]: '(dsp)',
};

/**
 * 断更时长离散档位。
 * 后端 no_update_hours_bucket 列就是档位值（24/48/72/96），多选走 IN(...) 离散匹配；空数组 = 不过滤。
 * label 走 i18n 用「区间」表达，避免分片入仓下"选 72h 没数但选 96h 有数"的歧义。
 */
type DurationOption = { value: number; labelKey: string };
const DURATION_OPTIONS_IN_HUB: DurationOption[] = [
    { value: 48, labelKey: 'between48And72' },
    { value: 72, labelKey: 'between72And96' },
    { value: 96, labelKey: 'gte96' },
];
const DURATION_OPTIONS_LAST_MILE: DurationOption[] = [
    { value: 24, labelKey: 'between24And48' },
    { value: 48, labelKey: 'between48And72' },
    { value: 72, labelKey: 'gte72' },
];

const ALL_STATUS_VALUES: NoUpdateStatusInput[] = [
    NoUpdateStatusInput.CURRENTLY,
    NoUpdateStatusInput.EVER_NOT_CURRENT,
    NoUpdateStatusInput.BEFORE_PENALTY,
];

/**
 * 「断更时长」→「是否断更」联动允许集合（与组件内 allowedBuckets 反向对称）。
 * 时长档位隐含了是否达判罚阈值的语义：≥阈值 档只可能是当前/曾断更，<阈值 档只可能是未到判罚时长。
 * 空数组或跨阈值混合选择 → 全部允许（不收窄），否则会把用户刚选的另一半档位判成非法。
 */
const computeAllowedStatuses = (
    buckets: number[],
    threshold: number,
): Set<NoUpdateStatusInput> => {
    if (buckets.length === 0) return new Set(ALL_STATUS_VALUES);
    const allowed = new Set<NoUpdateStatusInput>();
    buckets.forEach((v) => {
        if (v >= threshold) {
            allowed.add(NoUpdateStatusInput.CURRENTLY);
            allowed.add(NoUpdateStatusInput.EVER_NOT_CURRENT);
        } else {
            allowed.add(NoUpdateStatusInput.BEFORE_PENALTY);
        }
    });
    return allowed;
};

/** 「最后轨迹账号」「断更开始账号」列与导出 cell 的统一拼接：name(role)；name 为空时返回 fallback */
const formatAccountWithRole = (
    name: string | null | undefined,
    role: string | null | undefined,
    fallback: string,
): string => (name ? `${name}${role ? `(${role})` : ''}` : fallback);

export type WmsNoUpdateScope =
    | { kind: 'hub'; hubId: number }
    | { kind: 'whs'; whsId: number }
    | { kind: 'dsp'; dspId: number };

export interface WmsNoUpdateTableProps {
    /** 视角 scope */
    scope: WmsNoUpdateScope;
    /** Hub 页内 tab 上下文：仓内 / 尾程；Warehouse 页固定为 LAST_MILE */
    category: 'IN_HUB' | 'LAST_MILE';
}

const WmsNoUpdateTable: React.FC<WmsNoUpdateTableProps> = ({ scope, category }) => {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
    /** 多选，空数组 = 默认本 tab 全部 type（保留 tab 维度边界） */
    const [selectedTypes, setSelectedTypes] = useState<NoUpdateTypeGql[]>([]);
    /**
     * 多选时长档位，空数组 = 不过滤
     * 初始值与下方 selectedStatuses=[CURRENTLY] + useEffect 联动结果一致（IN_HUB→96 / LAST_MILE→72），
     * 避免首次渲染先发一次"不带 noUpdateHoursBuckets"的请求、再被 useEffect 修正后的请求覆盖（前者被 abort，DevTools 显示为失败）
     */
    const [selectedHoursBuckets, setSelectedHoursBuckets] = useState<number[]>(
        () => [category === 'IN_HUB' ? 96 : 72]
    );
    /** 多选是否当前断更，空数组 = 不过滤；同时选两个也等价不过滤 */
    const [selectedStatuses, setSelectedStatuses] = useState<NoUpdateStatusInput[]>([NoUpdateStatusInput.CURRENTLY]);
    /**
     * UI 层排序状态：列 key + ascend/descend；用 controlled `sortOrder` 与外部 state 保持一致。
     * 默认按"断更时长"DESC（首屏从高到低）。
     * 后端没有"断更时长精确列"，noUpdateHours 列映射到 noUpdateStartTime 反向排（startTime 越早 → 当前时长越长）。
     */
    const [uiSortKey, setUiSortKey] = useState<string>('noUpdateHours');
    const [uiSortDirection, setUiSortDirection] = useState<'ascend' | 'descend'>('descend');
    /** 多选断更地点 id（warehouse/dsp id），空数组 = 不过滤 */
    const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
    /**
     * 断更开始时间范围筛选：dayjs 小时取整；任一端 null = 该端不过滤。
     * 默认 [7 天前当前小时, 当前小时]；后端按半开区间 [start, end+1h) 过滤。
     */
    const [noUpdateStartTimeRange, setNoUpdateStartTimeRange] = useState<
        [Dayjs | null, Dayjs | null]
    >(() => [dayjs().subtract(7, 'day').startOf('hour'), dayjs().startOf('hour')]);
    /** 包裹详情抽屉 */
    const [orderDetailVisible, setOrderDetailVisible] = useState(false);
    const [drawerOrderId, setDrawerOrderId] = useState<string>('');

    const showOrderDetail = (orderId: number | null | undefined) => {
        if (orderId == null) return;
        setDrawerOrderId(String(orderId));
        setOrderDetailVisible(true);
    };

    /** 批量选择 + 异步导出 */
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<NoUpdateDetailRow[]>([]);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const [generateExport] = useMutation<
        GenerateWmsNoUpdateDetailDownloadReportResponse,
        GenerateWmsNoUpdateDetailDownloadReportVariables
    >(GENERATE_WMS_NO_UPDATE_DETAIL_DOWNLOAD_REPORT);

    const copyTrackingNumbers = async () => {
        if (selectedRows.length === 0) {
            message.warning(t('wms.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const tns = selectedRows
            .map((r) => r.trackingNumber?.trim())
            .filter((tn): tn is string => Boolean(tn && tn.length > 0));
        if (tns.length === 0) {
            message.warning(t('wms.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const text = tns.join('\n');
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            message.success(t('common.messages.copied'));
        } catch (err) {
            console.error('Copy tracking numbers failed:', err);
            message.error(t('common.messages.copyFailed'));
        }
    };

    const handleDownload = () => {
        void runExportWithCooldown(async () => {
            try {
                // 选中行：仅按 ids + scope 导出（其它筛选项忽略，与 SLA drawer 同语义）；未选：按当前 filter 全量导出
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const downloadFilter: WmsNoUpdateQueryFilterInput = selectedRows.length > 0
                    ? {
                        hubId: filter.hubId,
                        whsId: filter.whsId,
                        dspId: filter.dspId,
                        ids: selectedRows.map((r) => String(r.id)),
                    }
                    : { ...filter };
                const response = await generateExport({
                    variables: { filter: downloadFilter, timezone: userTimezone },
                });
                const fileName = response.data?.generateWmsNoUpdateDetailDownloadReport;
                if (fileName) {
                    setGeneratedFileName(fileName);
                    setDownloadModalVisible(true);
                } else {
                    message.error(t('wms.dashboard.noUpdate.drawer.exportFailed'));
                }
            } catch (err) {
                // 后端 BusinessException（如 DOWNLOAD_EXCEEDS_LIMIT 超过 10w 条）会带 messageKey + details，
                // showError 会按 i18n 翻译并展示具体最大值，避免"导出失败"通用 toast 让用户摸不到头脑
                console.error('Export no-update detail failed:', err);
                showError(err);
            }
        });
    };

    const typeOptionsRaw = category === 'IN_HUB' ? IN_HUB_TYPE_OPTIONS : LAST_MILE_TYPE_OPTIONS;
    const durationOptionsRaw = category === 'IN_HUB' ? DURATION_OPTIONS_IN_HUB : DURATION_OPTIONS_LAST_MILE;

    const typeOptions = useMemo(
        () => typeOptionsRaw.map((o) => ({
            label: t(`wms.dashboard.noUpdate.type.${o.labelKey}`),
            value: o.type,
        })),
        [typeOptionsRaw, t]
    );
    // 判罚阈值：仓内 96h / 尾程 72h；BEFORE_PENALTY 下限 = 本 tab 时长档位的最小起点（仓内 48h / 尾程 24h）
    const statusThreshold = category === 'IN_HUB' ? 96 : 72;
    const statusLowerBound = category === 'IN_HUB' ? 48 : 24;

    const allowedStatuses = useMemo(
        () => computeAllowedStatuses(selectedHoursBuckets, statusThreshold),
        [selectedHoursBuckets, statusThreshold]
    );

    // 「是否断更」→「断更时长」联动允许集合：
    // - 含 CURRENTLY/EVER_NOT_CURRENT（≥阈值语义）→ 加入 [阈值] 档
    // - 含 BEFORE_PENALTY（<阈值语义）→ 加入所有 <阈值 档
    // - selectedStatuses 为空 → 全部档可选（不联动）
    const allowedBuckets = useMemo<Set<number>>(() => {
        const all = new Set(durationOptionsRaw.map((o) => o.value));
        if (selectedStatuses.length === 0) return all;
        const allowed = new Set<number>();
        selectedStatuses.forEach((s) => {
            if (s === NoUpdateStatusInput.CURRENTLY || s === NoUpdateStatusInput.EVER_NOT_CURRENT) {
                allowed.add(statusThreshold);
            }
            if (s === NoUpdateStatusInput.BEFORE_PENALTY) {
                durationOptionsRaw.forEach((o) => {
                    if (o.value < statusThreshold) allowed.add(o.value);
                });
            }
        });
        return allowed;
    }, [selectedStatuses, durationOptionsRaw, statusThreshold]);

    const durationOptions = useMemo(
        () => durationOptionsRaw.map((o) => ({
            label: t(`wms.dashboard.noUpdate.duration.range.${o.labelKey}`),
            value: o.value,
            disabled: !allowedBuckets.has(o.value),
        })),
        [durationOptionsRaw, t, allowedBuckets]
    );

    // 是否断更 → 时长联动：切换 selectedStatuses 时
    // 1) 清掉已选时长里被禁用的档位（避免幽灵过滤）
    // 2) 单类（仅 ≥阈值 类，不含 BEFORE_PENALTY）且时长被清空 → 自动填 [阈值]，作为"默认 ≥阈值"视觉提示
    // 依赖不包含 selectedHoursBuckets：避免用户主动清空时长后被立刻填回的死循环
    useEffect(() => {
        if (selectedStatuses.length === 0) return;
        const onlyAboveThreshold = selectedStatuses.every(
            (s) => s === NoUpdateStatusInput.CURRENTLY || s === NoUpdateStatusInput.EVER_NOT_CURRENT
        );
        const filtered = selectedHoursBuckets.filter((v) => allowedBuckets.has(v));
        const next = (onlyAboveThreshold && filtered.length === 0) ? [statusThreshold] : filtered;
        const changed = next.length !== selectedHoursBuckets.length
            || next.some((v, i) => v !== selectedHoursBuckets[i]);
        if (changed) setSelectedHoursBuckets(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStatuses, allowedBuckets, statusThreshold]);
    const statusOptions = useMemo(
        () => [
            {
                label: t('wms.dashboard.noUpdate.status.currently', { threshold: statusThreshold }),
                value: NoUpdateStatusInput.CURRENTLY,
                disabled: !allowedStatuses.has(NoUpdateStatusInput.CURRENTLY),
            },
            {
                label: t('wms.dashboard.noUpdate.status.everNotCurrent', { threshold: statusThreshold }),
                value: NoUpdateStatusInput.EVER_NOT_CURRENT,
                disabled: !allowedStatuses.has(NoUpdateStatusInput.EVER_NOT_CURRENT),
            },
            {
                label: t('wms.dashboard.noUpdate.status.beforePenalty', {
                    threshold: statusThreshold,
                    lowerBound: statusLowerBound,
                }),
                value: NoUpdateStatusInput.BEFORE_PENALTY,
                disabled: !allowedStatuses.has(NoUpdateStatusInput.BEFORE_PENALTY),
            },
        ],
        [t, statusThreshold, allowedStatuses]
    );

    const filter: WmsNoUpdateQueryFilterInput = useMemo(() => {
        // LAST_MILE 「≥72h」在 DB 中横跨两个 bucket（72=[72,96)、96=≥96），选 72 需展开为 [72, 96]
        const expandedHoursBuckets = category === 'LAST_MILE' && selectedHoursBuckets.includes(72)
            ? Array.from(new Set([...selectedHoursBuckets, 96]))
            : selectedHoursBuckets;
        // 「是否断更」留空时 UI 显示"全部"，但时长档位已隐含阈值语义，隐式按该语义传参，
        // 否则会发出"时长 ≥阈值 + 状态全部"的自相矛盾查询
        const impliedStatuses = allowedStatuses.size < ALL_STATUS_VALUES.length
            ? ALL_STATUS_VALUES.filter((s) => allowedStatuses.has(s))
            : [];
        const base: WmsNoUpdateQueryFilterInput = {
            // 空数组 → 默认本 tab 全部 type（保留 tab 维度边界）；否则 IN(selectedTypes)
            noUpdateTypes: selectedTypes.length === 0 ? typeOptionsRaw.map((o) => o.type) : selectedTypes,
            noUpdateLocationIds: selectedLocationIds.length > 0 ? selectedLocationIds : undefined,
            noUpdateHoursBuckets: expandedHoursBuckets.length > 0 ? expandedHoursBuckets : undefined,
            noUpdateStatuses: selectedStatuses.length > 0
                ? selectedStatuses
                : (impliedStatuses.length > 0 ? impliedStatuses : undefined),
        };
        if (scope.kind === 'hub') base.hubId = scope.hubId;
        if (scope.kind === 'whs') base.whsId = scope.whsId;
        if (scope.kind === 'dsp') base.dspId = scope.dspId;
        // 范围筛选：start 取小时，end 取小时再 +1h 形成半开区间 [start, end+1h)
        const [rangeStart, rangeEnd] = noUpdateStartTimeRange;
        if (rangeStart) {
            base.noUpdateStartTimeStart = rangeStart.startOf('hour').toISOString();
        }
        if (rangeEnd) {
            base.noUpdateStartTimeEnd = rangeEnd.startOf('hour').add(1, 'hour').toISOString();
        }
        return base;
    }, [
        scope,
        category,
        selectedTypes,
        selectedLocationIds,
        selectedHoursBuckets,
        selectedStatuses,
        allowedStatuses,
        typeOptionsRaw,
        noUpdateStartTimeRange,
    ]);

    /**
     * UI 排序 → 后端 sortBy/sortDirection。
     * noUpdateHours 后端按 COALESCE(end, now) - start 实时计算排序，与展示口径一致，直接透传。
     */
    const { sortField, sortDirection } = useMemo<{ sortField: string; sortDirection: 'ASC' | 'DESC' }>(
        () => ({
            sortField: uiSortKey,
            sortDirection: uiSortDirection === 'ascend' ? 'ASC' : 'DESC',
        }),
        [uiSortKey, uiSortDirection]
    );

    const detailInput: WmsNoUpdateDetailPageRequestInput = useMemo(
        () => ({
            filter,
            page,
            size: pageSize,
            sortBy: sortField,
            sortDirection,
        }),
        [filter, page, pageSize, sortField, sortDirection]
    );

    const { data: detailData, loading: detailLoading } = useQuery<QueryWmsNoUpdateDetailListResponse>(
        QUERY_WMS_NO_UPDATE_DETAIL_LIST,
        {
            variables: { input: detailInput },
            fetchPolicy: 'cache-and-network',
        }
    );

    const detailListResp = detailData?.queryWmsNoUpdateDetailList;
    const content = detailListResp?.content ?? [];
    const total = detailListResp?.totalElements ?? 0;
    const accessiblePages = detailListResp?.accessiblePages ?? 0;

    /**
     * 断更地点下拉：按 scope 走独立 GraphQL（明细表 _a/_c 安全查询去重所有 (type, id)，
     * 回查 warehouse/dsp 拿名字）。与列表筛选/分页解耦，避免"只能选当前页里出现过的地点"。
     */
    const locationOptionsInput: WmsNoUpdateLocationOptionsInput = useMemo(() => {
        const base: WmsNoUpdateLocationOptionsInput = {};
        if (scope.kind === 'hub') base.hubId = scope.hubId;
        if (scope.kind === 'whs') base.whsId = scope.whsId;
        if (scope.kind === 'dsp') base.dspId = scope.dspId;
        // 与主列表查询使用同一份 noUpdateTypes：用户选了具体 type 后，下拉只展示对应 type 出现过的地点
        // （例如选「仓内作业」后地点收敛到本 hub；选「尾程」后地点是 WHS/DSP）
        base.noUpdateTypes = filter.noUpdateTypes ?? undefined;
        return base;
    }, [scope, filter.noUpdateTypes]);

    const { data: locationOptionsData, previousData: locationOptionsPrevData } =
        useQuery<QueryWmsNoUpdateLocationOptionsResponse>(
            QUERY_WMS_NO_UPDATE_LOCATION_OPTIONS,
            {
                variables: { input: locationOptionsInput },
                fetchPolicy: 'cache-and-network',
            }
        );

    // 用 previousData 兜底：cache-and-network 刷新瞬间 data 可能为 undefined，导致下拉 options 闪空、
    // 已选中的 value 找不到 label 退化成数字 id。优先用最新 data，缺失时回退上一次成功结果。
    const locationOptions = useMemo(() => {
        const rows = locationOptionsData?.queryWmsNoUpdateLocationOptions
            ?? locationOptionsPrevData?.queryWmsNoUpdateLocationOptions
            ?? [];
        return rows.map((o) => ({
            label: `${LOCATION_TYPE_PREFIX[o.type] ?? ''} ${o.name}`.trim(),
            value: o.id,
        }));
    }, [locationOptionsData, locationOptionsPrevData]);

    const sortingSeg = (row: NoUpdateDetailRow, idx: 0 | 1 | 2): string => {
        const parts = row.sortingCode?.split('-') ?? [];
        return parts[idx] || '-';
    };
    const columns: ColumnsType<NoUpdateDetailRow> = useMemo(
        () => [
            {
                title: t('wms.dashboard.noUpdate.column.trackingNumber'),
                dataIndex: 'trackingNumber',
                width: 160,
                fixed: 'left',
                render: (text: string, row: NoUpdateDetailRow) => (
                    <Button
                        type="link"
                        onClick={() => showOrderDetail(row.orderId)}
                        style={{ paddingLeft: 0, whiteSpace: 'nowrap' }}
                    >
                        {text}
                    </Button>
                ),
            },
            { title: t('wms.dashboard.noUpdate.column.parcelStatus'), dataIndex: 'parcelStatus', width: 130, fixed: 'left', render: (v) => (v ? t('enum.overallStatus.' + v, { defaultValue: v }) : '-') },
            { title: t('wms.dashboard.noUpdate.column.bagNumber'), dataIndex: 'bagNumber', width: 140, render: (v) => v ?? '-' },
            { title: t('wms.dashboard.noUpdate.column.responsibleHub'), dataIndex: 'hubName', key: 'hubName', width: 120, render: (v) => v ?? '-' },
            { title: t('wms.dashboard.noUpdate.column.destinationHub'), key: 'destinationHub', width: 120, render: (_: unknown, row: NoUpdateDetailRow) => sortingSeg(row, 0) },
            { title: t('wms.dashboard.noUpdate.column.destinationWarehouse'), key: 'destinationWarehouse', width: 140, render: (_: unknown, row: NoUpdateDetailRow) => sortingSeg(row, 1) },
            { title: t('wms.dashboard.noUpdate.column.destinationRoute'), key: 'destinationRoute', width: 130, render: (_: unknown, row: NoUpdateDetailRow) => sortingSeg(row, 2) },
            { title: t('wms.dashboard.noUpdate.column.dspName'), dataIndex: 'dspName', width: 120, render: (v) => v ?? '-' },
            { title: t('wms.dashboard.noUpdate.column.driverName'), dataIndex: 'driverName', width: 120, render: (v) => v ?? '-' },
            { title: t('wms.dashboard.noUpdate.column.lastTrackingEvent'), dataIndex: 'lastTrackingEventName', width: 140, render: (v) => (v ? t('enum.orderEvent.' + stripOrderEventDataPrefix(v), { defaultValue: v }) : '-') },
            {
                title: t('wms.dashboard.noUpdate.column.lastTrackingAccount'),
                key: 'lastTrackingAccount',
                width: 180,
                render: (_: unknown, row: NoUpdateDetailRow) =>
                    formatAccountWithRole(row.lastTrackingAccountName, row.lastTrackingAccountRole, '-'),
            },
            { title: t('wms.dashboard.noUpdate.column.lastTrackingTime'), dataIndex: 'lastTrackingTime', width: 160, render: (v) => formatUtcToLocal(v) },
            {
                title: t('wms.dashboard.noUpdate.column.type'),
                key: 'noUpdateType',
                width: 140,
                render: (_: unknown, row: NoUpdateDetailRow) => {
                    if (!row.noUpdateType) return '-';
                    const label = t(
                        `wms.dashboard.noUpdate.type.${TYPE_LABEL_KEY[row.noUpdateType] ?? row.noUpdateType}`,
                        { defaultValue: row.noUpdateType },
                    );
                    return <Tag color={TYPE_TAG_COLOR[row.noUpdateType]}>{label}</Tag>;
                },
            },
            {
                title: t('wms.dashboard.noUpdate.column.responsibleSite'),
                dataIndex: 'responsibleSiteName',
                width: 120,
                render: (v) => v ?? '-',
            },
            {
                title: t('wms.dashboard.noUpdate.column.location'),
                dataIndex: 'noUpdateLocationName',
                width: 120,
                render: (v) => v ?? '-',
            },
            { title: t('wms.dashboard.noUpdate.column.noUpdateStartEvent'), dataIndex: 'noUpdateStartEventName', width: 140, render: (v) => (v ? t('enum.orderEvent.' + stripOrderEventDataPrefix(v), { defaultValue: v }) : '-') },
            {
                title: t('wms.dashboard.noUpdate.column.noUpdateStartAccount'),
                key: 'noUpdateStartAccount',
                width: 180,
                render: (_: unknown, row: NoUpdateDetailRow) =>
                    formatAccountWithRole(row.noUpdateStartAccountName, row.noUpdateStartAccountRole, '-'),
            },
            {
                title: t('wms.dashboard.noUpdate.column.noUpdateStartTime'),
                dataIndex: 'noUpdateStartTime',
                key: 'noUpdateStartTime',
                width: 160,
                sorter: true,
                sortOrder: uiSortKey === 'noUpdateStartTime' ? uiSortDirection : undefined,
                sortDirections: ['ascend', 'descend'],
                render: (v) => formatUtcToLocal(v),
            },
            {
                title: t('wms.dashboard.noUpdate.column.noUpdateEndTime'),
                dataIndex: 'noUpdateEndTime',
                key: 'noUpdateEndTime',
                width: 160,
                sorter: true,
                sortOrder: uiSortKey === 'noUpdateEndTime' ? uiSortDirection : undefined,
                sortDirections: ['ascend', 'descend'],
                render: (v) => (v ? formatUtcToLocal(v) : t('wms.dashboard.noUpdate.tag.currentlyNoUpdate')),
            },
            {
                title: t('wms.dashboard.noUpdate.column.noUpdateHours'),
                dataIndex: 'noUpdateHours',
                key: 'noUpdateHours',
                width: 100,
                sorter: true,
                sortOrder: uiSortKey === 'noUpdateHours' ? uiSortDirection : undefined,
                sortDirections: ['descend', 'ascend'],
            },
            {
                title: t('wms.dashboard.noUpdate.column.isCurrentlyNoUpdate'),
                dataIndex: 'isCurrentlyNoUpdate',
                width: 100,
                render: (v: number) => {
                    if (v === 1) return <Tag color="blue">{t('wms.dashboard.noUpdate.tag.currentlyNoUpdate')}</Tag>;
                    if (v === -1) return <Tag color="orange">{t('wms.dashboard.noUpdate.tag.beforePenalty')}</Tag>;
                    return <Tag>{t('wms.dashboard.noUpdate.tag.everNoUpdate')}</Tag>;
                },
            },
        ],
        [t, uiSortKey, uiSortDirection]
    );

    /** 允许参与排序的列 key（与 columns 中声明了 `sorter: true` 的列保持一致） */
    const SORTABLE_KEYS = ['noUpdateStartTime', 'noUpdateEndTime', 'noUpdateHours'];

    // 每列单独计算 label 宽度，避免被同行其它列的长 label 拉宽产生多余留白
    // grid-cols-3 下 5 个筛选项排列（顺序：时间 → 类型 → 地点 → 是否断更 → 时长）：
    //   row1: col1=时间   col2=类型   col3=地点
    //   row2: col4=是否断更 col5=时长
    // 同列对：col1↔col4（时间/是否断更）、col2↔col5（类型/时长）、col3 单独（地点）
    const colStartTimeStatusSpanClassName = getSmartLabelClassName([
        t('wms.dashboard.noUpdate.filter.startTime'),
        t('wms.dashboard.noUpdate.filter.currently'),
    ]);
    const colTypeDurationSpanClassName = getSmartLabelClassName([
        t('wms.dashboard.noUpdate.filter.type'),
        t('wms.dashboard.noUpdate.filter.duration'),
    ]);
    const colLocationSpanClassName = getSmartLabelClassName([t('wms.dashboard.noUpdate.filter.location')]);
    const startTimeLabelClassName = useFilterLabelClassName(colStartTimeStatusSpanClassName);
    const currentlyLabelClassName = useFilterLabelClassName(colStartTimeStatusSpanClassName);
    const typeLabelClassName = useFilterLabelClassName(colTypeDurationSpanClassName);
    const durationLabelClassName = useFilterLabelClassName(colTypeDurationSpanClassName);
    const locationLabelClassName = useFilterLabelClassName(colLocationSpanClassName);
    /** 中文水平、外语垂直；min-w-0 防止列被长内容撑开 */
    const containerClassName = useFilterContainerClassName('min-w-0');
    const inputContainerClassName = useFilterInputContainerClassName();

    return (
        <div>
            {/* 指标卡（按大包进度样式）：总数与列表 totalElements 共用同一份 count，无需单独 stats 接口 */}
            <div className="mb-3 grid gap-3 grid-cols-2">
                <div className="rounded-lg shadow-sm border border-violet-200/70 p-4 bg-violet-50">
                    <div className="text-sm text-gray-700 font-semibold">{t('wms.dashboard.noUpdate.stats.totalCount')}</div>
                    <div className="mt-1 text-3xl font-extrabold tabular-nums text-gray-800">
                        {detailLoading ? '-' : total.toLocaleString()}
                    </div>
                </div>
            </div>

            {/* 筛选区（UI 规范：grid-cols-3 + 中文水平 / 外语垂直 + size=middle + 智能 label 宽度）
                顺序：断更开始时间 → 断更类型 → 断更地点 → 是否断更 → 断更时长（用户指定） */}
            <div className="mb-2 py-2 grid gap-x-10 gap-y-4 grid-cols-[1.15fr_1fr_1fr]">
                <div className={containerClassName}>
                    <span className={startTimeLabelClassName}>{t('wms.dashboard.noUpdate.filter.startTime')}</span>
                    <div className={inputContainerClassName}>
                        <RangePicker
                            className="w-full"
                            size="middle"
                            allowEmpty={[true, true]}
                            showTime={{ format: 'HH' }}
                            format="YYYY/MM/DD HH"
                            placeholder={[
                                t('wms.dashboard.noUpdate.filter.startTimeRangeStart') as string,
                                t('wms.dashboard.noUpdate.filter.startTimeRangeEnd') as string,
                            ]}
                            value={noUpdateStartTimeRange}
                            onChange={(range) => {
                                setNoUpdateStartTimeRange(range ?? [null, null]);
                                setPage(1);
                            }}
                        />
                    </div>
                </div>
                <div className={containerClassName}>
                    <span className={typeLabelClassName}>{t('wms.dashboard.noUpdate.filter.type')}</span>
                    <div className={inputContainerClassName}>
                        <Select
                            className="w-full"
                            size="middle"
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            maxTagTextLength={10}
                            placeholder={t('wms.dashboard.noUpdate.filter.all')}
                            value={selectedTypes}
                            onChange={(v: NoUpdateTypeGql[]) => {
                                setSelectedTypes(v ?? []);
                                // 联动清空地点：老 id 可能不在新 type 对应的地点集合里，留着会变成幽灵过滤
                                setSelectedLocationIds([]);
                                setPage(1);
                            }}
                            options={typeOptions}
                        />
                    </div>
                </div>
                <div className={containerClassName}>
                    <span className={locationLabelClassName}>{t('wms.dashboard.noUpdate.filter.location')}</span>
                    <div className={inputContainerClassName}>
                        <Select
                            className="w-full"
                            size="middle"
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            maxTagTextLength={10}
                            showSearch
                            optionFilterProp="label"
                            placeholder={t('wms.dashboard.noUpdate.filter.locationPlaceholder')}
                            value={selectedLocationIds}
                            onChange={(v: number[]) => {
                                setSelectedLocationIds(v ?? []);
                                setPage(1);
                            }}
                            options={locationOptions}
                        />
                    </div>
                </div>
                <div className={containerClassName}>
                    <span className={currentlyLabelClassName}>{t('wms.dashboard.noUpdate.filter.currently')}</span>
                    <div className={inputContainerClassName}>
                        <Select
                            className="w-full"
                            size="middle"
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            maxTagTextLength={10}
                            placeholder={t('wms.dashboard.noUpdate.filter.all')}
                            value={selectedStatuses}
                            onChange={(v: NoUpdateStatusInput[]) => {
                                setSelectedStatuses(v ?? []);
                                setPage(1);
                            }}
                            options={statusOptions}
                        />
                    </div>
                </div>
                <div className={containerClassName}>
                    <span className={durationLabelClassName}>{t('wms.dashboard.noUpdate.filter.duration')}</span>
                    <div className={inputContainerClassName}>
                        <Select
                            className="w-full"
                            size="middle"
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            maxTagTextLength={10}
                            placeholder={t('wms.dashboard.noUpdate.filter.all')}
                            value={selectedHoursBuckets}
                            onChange={(v: number[]) => {
                                const next = v ?? [];
                                setSelectedHoursBuckets(next);
                                // 新档位可能让已选状态变 disabled，而 disabled 的 tag 没有关闭按钮、却仍在过滤
                                const allowed = computeAllowedStatuses(next, statusThreshold);
                                const kept = selectedStatuses.filter((s) => allowed.has(s));
                                if (kept.length !== selectedStatuses.length) {
                                    setSelectedStatuses(kept);
                                }
                                setPage(1);
                            }}
                            options={durationOptions}
                        />
                    </div>
                </div>
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-2">
                {content[0]?.calculatedAt ? (
                    <span className="text-sm text-red-500 font-bold">
                        {t('wms.dashboard.noUpdate.filter.dataUpdatedAt')} {formatUtcToLocal(content[0].calculatedAt)}
                    </span>
                ) : null}
                <div className="flex flex-wrap gap-2 ml-auto">
                    <Button
                        type="primary"
                        onClick={copyTrackingNumbers}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('common.buttons.copyTracking')}</span>
                    </Button>
                    <Button
                        type="primary"
                        loading={exportLoading}
                        onClick={handleDownload}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">
                            {getExportButtonLabel(t('components.button.download'))}
                        </span>
                    </Button>
                </div>
            </div>

            <Table<NoUpdateDetailRow>
                rowKey="id"
                columns={columns}
                dataSource={content}
                loading={detailLoading}
                pagination={false}
                rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys,
                    onChange: (keys, rows) => {
                        setSelectedRowKeys(keys);
                        setSelectedRows(rows);
                    },
                }}
                onChange={(_p, _f, sorter, extra) => {
                    if (extra.action !== 'sort') return;
                    const s = Array.isArray(sorter) ? sorter[0] : sorter;
                    const order = s?.order as ('ascend' | 'descend' | undefined);
                    const columnKey = s?.columnKey as (string | undefined);
                    // 取消排序：回到默认「断更时长 DESC」
                    if (!order || !columnKey) {
                        setUiSortKey('noUpdateHours');
                        setUiSortDirection('descend');
                        setPage(1);
                        return;
                    }
                    if (!SORTABLE_KEYS.includes(columnKey)) return;
                    setUiSortKey(columnKey);
                    setUiSortDirection(order);
                    setPage(1);
                }}
                scroll={{ x: 'max-content' }}
                size="middle"
            />

            <TablePaginationBar
                className="mt-3"
                total={total}
                current={page}
                pageSize={pageSize}
                onChange={(p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                }}
                maxPages={accessiblePages}
            />

            <CentralParcelDetailDrawer
                visible={orderDetailVisible}
                onClose={() => setOrderDetailVisible(false)}
                orderId={drawerOrderId}
                userRole={getUserInfo()?.account?.role?.name || ''}
            />

            <AsyncReportDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.wmsNoUpdate}
            />
        </div>
    );
};

export default WmsNoUpdateTable;
